import { useState, useEffect } from "react";
import {
  Drawer,
  Box,
  IconButton,
  CircularProgress,
  Typography
} from "@mui/material";
import { useFormik } from "formik";
import profilecross from "../../../../assets/profilecross.svg";
import CustomText, { textStyles } from "../../../../components/CustomText";
import MultiSelectDropdown from "../../../../components/MultiSelectionDropdown";
import StatusDropdown from "../../../../components/StatusDropdown";
import CustomButton from "../../../../components/CustomButton";
import { useTranslation } from "react-i18next";
import { BASE_URL } from "../../../../components/BaseUrl";
import { useSelector } from "react-redux";
import { decryptToken } from "../../../../Utlis/Encryption";
import { toast } from "react-hot-toast";
import document1 from "../../../../assets/document1.svg";
import {
  convertLocalDateTimeToUTC,
  convertUTCToLocalDateTime,
} from "../../../../components/dateTimeUtils.js";

const EditDrawer = ({ open, onClose, row, onSuccess }) => {
  const { t } = useTranslation();
  const token = useSelector((state) => state.auth.token);
  const decryptedToken = token ? decryptToken(token) : null;
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const { company } = useSelector((state) => state.auth);
  const companyId = company?.id;

  const [workers, setWorkers] = useState([]);
  const formatDateTime = (dateString) => {
    const formatted = convertUTCToLocalDateTime(dateString, {
      locale: "en-GB",
      formatOptions: {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      },
    });
    return formatted === "—" ? "-" : formatted;
  };

  // Convert a stored UTC ISO timestamp into the "YYYY-MM-DD" format
  // an <input type="date"> expects (in the browser's local time) —
  // convertLocalDateTimeToUTC() converts it back to UTC on submit.
  const toDatetimeLocalValue = (isoString) => {
    if (!isoString) return "";
    const d = new Date(isoString);
    if (isNaN(d.getTime())) return "";
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  };

  // Use row data directly - no API fetch needed
  useEffect(() => {
    if (open && row) {
      // Console log the row data from table
      console.log("📄 Edit Drawer - Row Data from Table:", row);
      console.log("📋 All Row Fields:", {
        id: row.id,
        company_id: row.company_id,
        direction: row.direction,
        owner_type: row.owner_type,
        owner_id: row.owner_id,
        name: row.name,
        documentName: row.documentName,
        subject: row.subject,
        description: row.description,
        file_id: row.file_id,
        file_type: row.file_type,
        fileType: row.fileType,
        category: row.category,
        access_mode: row.access_mode,
        status: row.status,
        expires_at: row.expires_at,
        created_at: row.created_at,
        createdAt: row.createdAt,
        updated_at: row.updated_at,
        deleted_at: row.deleted_at,
        document_url: row.document_url,
        fileUrl: row.fileUrl,
        has_access: row.has_access,
        recipient_worker_ids: row.recipient_worker_ids,
        owner: row.owner,
      });
    }
  }, [open, row]);

  // Fetch workers
  useEffect(() => {
    const fetchWorkers = async () => {
      try {
        const res = await fetch(
          `${BASE_URL}/public/workers?no_pagination=true&company_id=${companyId}&status=active`,
          {
            headers: {
              Authorization: `Bearer ${decryptedToken}`,
            },
          }
        );
        if (!res.ok) throw new Error("Failed to fetch workers");
        const data = await res.json();

        setWorkers([
          { label: "All", value: "All" },
          ...(data?.data?.records || []).map((w) => ({
            label: `${w.first_name} ${w.last_name} (${w.email})`,
            value: w.id,
          })),
        ]);
      } catch (err) {
        console.error("❌ Error fetching workers:", err);
      }
    };
    if (decryptedToken && open) fetchWorkers();
  }, [decryptedToken, companyId, open]);

  // Get recipients from row data
  const getRecipients = () => {
    if (!row) return [];
    
    // Get recipient_worker_ids from the row data
    const recipientWorkerIds = row.recipient_worker_ids || [];

    // Only treat this as "All" when the document's access_mode actually is
    // "all" — a "specific" document with zero recipients (a stale/broken
    // state from before employees were required in Add) should show as
    // empty, not be silently displayed as "All".
    if (row.access_mode === "all") {
      return ["All"];
    }
    
    // Keep IDs as-is (numbers) — the worker dropdown options use w.id
    // (numeric) as their value, and MultiSelectDropdown matches chips by
    // strict equality, so stringifying here broke the match and fell back
    // to displaying the raw ID instead of the worker's name.
    return recipientWorkerIds;
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      docName: row?.documentName || row?.name || "",
      subject: row?.subject || "",
      description: row?.description || "",
      selectedWorkers: getRecipients(),
      file: null,
      fileUrl: null,
      expires_at: toDatetimeLocalValue(row?.expires_at),
      access_mode: row?.access_mode || "all",
      status: row?.status || "active",
    },
    validate: (values) => {
      const errors = {};
      if (!values.docName || (typeof values.docName === "string" && values.docName.trim() === "")) {
        errors.docName = t("addDocument.validation.docNameRequired") || "Document name is required";
      }
      if (!values.subject || (typeof values.subject === "string" && values.subject.trim() === "")) {
        errors.subject = t("addDocument.validation.subjectRequired") || "Subject is required";
      }
      if (!values.selectedWorkers || values.selectedWorkers.length === 0) {
        errors.selectedWorkers = t("addDocument.validation.workersRequired") || "Select at least one employee or \"All\"";
      }
      return errors;
    },
    validateOnBlur: true,
    validateOnChange: true,
    onSubmit: async (values) => {
      setLoading(true);
      try {
        const body = {
          name: values.docName?.trim() || "",
          subject: values.subject?.trim() || "",
          description: values.description?.trim() || "",
          status: values.status || "active",
          recipients: values.selectedWorkers.includes("All")
            ? []
            : values.selectedWorkers.map((w) => {
                const found = (workers || []).find((worker) => worker.value === w);
                return found?.value || w;
              }),
        };

        // Only include file_id and file_type if a new file was uploaded
        if (values.fileUrl) {
          body.file_id = values.fileUrl;
          body.file_type = "pdf";
        }

        // Include expires_at if provided
        if (values.expires_at) {
          body.expires_at = convertLocalDateTimeToUTC(values.expires_at) || values.expires_at;
        }

        console.log("📤 Update payload:", body);

        const res = await fetch(`${BASE_URL}/documents/${row.id}`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${decryptedToken}`,
          },
          body: JSON.stringify(body),
        });

        const data = await res.json();
        if (!res.ok || data.error) {
          const errorMessage = data.message || data.error || "Failed to update document";
          console.error("❌ API Error Response:", data);
          throw new Error(errorMessage);
        }

        toast.success(data.message || "Document updated successfully");
        console.log("✅ Document updated:", data);
        if (onSuccess) onSuccess();

        formik.resetForm();
        onClose();
      } catch (err) {
        console.error("❌ Error updating document:", err);
        toast.error(err.message || "Failed to update document");
      } finally {
        setLoading(false);
      }
    },
  });

  useEffect(() => {
    if (!open) {
      formik.resetForm();
    }
  }, [open]);

  const handleDropdownChange = ({ target: { name, value } }) => {
    let newValue = value;
    if (value.includes("All")) {
      newValue = ["All"];
    } else {
      newValue = value.filter((v) => v !== "All");
    }
    // Return the promise so callers can await it before marking the field
    // touched — otherwise touched-triggered validation runs against the
    // stale (pre-update) value.
    return formik.setFieldValue(name, newValue);
  };

  // Backend's /upload/pdf enforces a hard 10MB limit (upload.routes.js) —
  // mirror it client-side so oversized files are rejected instantly with a
  // clear message instead of silently uploading and failing.
  const MAX_PDF_SIZE_BYTES = 10 * 1024 * 1024;

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.type !== "application/pdf") {
      toast.error("Only PDF files are allowed");
      return;
    }

    if (file.size > MAX_PDF_SIZE_BYTES) {
      const message =
        t("addDocument.validation.pdfSizeExceeded") ||
        "PDF file size must be less than 10MB";
      toast.error(message);
      formik.setFieldError("file", message);
      formik.setFieldTouched("file", true, false);
      return;
    }

    const formData = new FormData();
    formData.append("pdf", file);

    try {
      setUploading(true);
      const res = await fetch(`${BASE_URL}/upload/pdf`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${decryptedToken}`,
        },
        body: formData,
      });

      const data = await res.json().catch(() => null);

      if (!res.ok) {
        throw new Error(data?.message || "Upload failed");
      }

      const uploadedUrl = data?.data?.url;

      formik.setFieldValue("file", file);
      formik.setFieldValue("fileUrl", uploadedUrl);
      formik.setFieldError("file", undefined);
      formik.setFieldTouched("file", true, false);

      toast.success(data?.message || "PDF uploaded successfully");
    } catch (err) {
      console.error("❌ Upload error:", err);
      toast.error(err.message || "Failed to upload file");
      formik.setFieldError("file", err.message || "Failed to upload file");
      formik.setFieldValue("file", null);
      formik.setFieldValue("fileUrl", null);
    } finally {
      setUploading(false);
    }
  };


  if (!row) return null;

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: {
            width: 540,
            maxWidth: "100%",
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 2,
            py: 1.5,
            borderBottom: "1px solid #E0E0E0",
            backgroundColor: "#f5f6f7",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <IconButton onClick={onClose}>
              <img src={profilecross} alt="close" />
            </IconButton>
            <CustomText
              sx={{
                ...textStyles.h1,
                fontSize: 18,
                fontWeight: 600,
                color: "#172B4D",
              }}
            >
              {t("common.edit") || "Edit Document"}
            </CustomText>
          </Box>
        </Box>

        {/* Body */}
        <Box sx={{ p: 3, flexGrow: 1, overflowY: "auto" }}>
          <>
          <CustomText
            sx={{
              ...textStyles.h1,
              fontSize: 18,
              fontWeight: 600,
              mb: 2,
              color: "#172B4D",
              textAlign: "left",
            }}
          >
            {t("addDocument.detailsHeading") || "Document Details"}
          </CustomText>

          {/* Doc Name */}
          <Box mb={2}>
            <label className="field_label">
              {t("addDocument.form.docName")} <span className="red_asterisk">*</span>
            </label>
            <input
              type="text"
              name="docName"
              value={formik.values.docName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              className="project_input"
              style={{
                width: "100%",
                marginTop: 6,
                height: "42px",
                border: formik.touched.docName && formik.errors.docName
                  ? "2px solid #d32f2f"
                  : "2px solid #091E4224"
              }}
            />
            {formik.touched.docName && formik.errors.docName && (
              <span style={{ color: "#d32f2f", fontSize: "12px", marginTop: "4px", display: "block" }}>
                {formik.errors.docName}
              </span>
            )}
          </Box>

          {/* Subject */}
          <Box mb={2}>
            <label className="field_label">
              {t("addDocument.form.subject")} <span className="red_asterisk">*</span>
            </label>
            <input
              type="text"
              name="subject"
              value={formik.values.subject}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              className="project_input"
              style={{
                width: "100%",
                marginTop: 6,
                height: "42px",
                border: formik.touched.subject && formik.errors.subject
                  ? "2px solid #d32f2f"
                  : "2px solid #091E4224"
              }}
            />
            {formik.touched.subject && formik.errors.subject && (
              <span style={{ color: "#d32f2f", fontSize: "12px", marginTop: "4px", display: "block" }}>
                {formik.errors.subject}
              </span>
            )}
          </Box>

          {/* Workers */}
          <Box mb={2}>
            <MultiSelectDropdown
              label={<>{t("addDocument.form.workers")} <span className="red_asterisk" style={{ color: "red" }}>*</span></>}
              name="selectedWorkers"
              values={formik.values.selectedWorkers}
              onChange={async (e) => {
                await handleDropdownChange(e);
                formik.setFieldTouched("selectedWorkers", true);
              }}
              options={workers}
            />
            {formik.touched.selectedWorkers && formik.errors.selectedWorkers && (
              <span style={{ color: "#d32f2f", fontSize: "12px", marginTop: "4px", display: "block" }}>
                {formik.errors.selectedWorkers}
              </span>
            )}
          </Box>

          {/* Status */}
          <Box mb={2}>
            <label className="field_label">
              {t("documentsTable.filter.status") || "Status"} <span className="red_asterisk">*</span>
            </label>
            <Box sx={{ mt: 1 }}>
              <StatusDropdown
                currentStatus={formik.values.status || "active"}
                onChange={(newStatus) => {
                  formik.setFieldValue("status", newStatus);
                }}
                statusOptions={["active", "expired", "revoked"]}
              />
            </Box>
          </Box>

          {/* Description */}
          <Box mb={2}>
            <label className="field_label">{t("addDocument.form.description")}</label>
            <textarea
              name="description"
              rows={4}
              className="project_input"
              style={{ width: "100%", marginTop: 6 }}
              value={formik.values.description}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
          </Box>
          <Box mb={2}>
            <label className="field_label">
              {t("documentsTable.th.expiresAt") || "Expires At"} ({t("common.optional") || "Optional"})
            </label>
            <input
              type="date"
              name="expires_at"
              value={formik.values.expires_at}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              className="project_input"
              style={{ width: "100%", marginTop: 6, height: 40 }}
            />
          </Box>

          {/* File Upload (Optional - to replace existing file) */}
          <Box mb={2}>
            <CustomText
              sx={{
                fontSize: "14px",
                fontWeight: 500,
                color: "#6B778C",
                mb: 0.5,
              }}
            >
              {t("addDocument.form.pdf")} ({t("common.optional") || "Optional"})
            </CustomText>
            <Box
              sx={{
                border: formik.touched.file && formik.errors.file
                  ? "2px dashed #d32f2f"
                  : "2px dashed #D0D5DD",
                borderRadius: 2,
                p: 3,
                textAlign: "center",
                backgroundColor: "#F9FAFB",
                cursor: "pointer",
                mb: 0.5,
                height: 160,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                position: "relative",
              }}
            >
              {uploading ? (
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5 }}>
                  <CircularProgress size={28} sx={{ color: "#006EC2" }} />
                  <CustomText sx={{ fontSize: 14, fontWeight: 500, color: "#6B778C" }}>
                    {t("addDocument.form.uploading") || "Uploading..."}
                  </CustomText>
                </Box>
              ) : !formik.values.file && (row?.document_url || row?.fileUrl) ? (
                // No new file picked yet, but the document already has one uploaded —
                // show it (with a way to view or replace it) instead of an empty prompt.
                <label htmlFor="pdf-upload-edit" style={{ cursor: "pointer", width: "100%" }}>
                  <input
                    type="file"
                    id="pdf-upload-edit"
                    accept="application/pdf"
                    onChange={handleFileUpload}
                    hidden
                  />
                  <Box sx={{ textAlign: "center" }}>
                    <img src={document1} alt="PDF" style={{ width: 45 }} />
                    <CustomText sx={{ fontSize: 14, wordBreak: "break-all" }}>
                      {row.documentName || row.name || (row.document_url || row.fileUrl || "").split("/").pop()}
                    </CustomText>
                    <Box sx={{ display: "flex", justifyContent: "center", gap: 2, mt: 1 }}>
                      <CustomText
                        component="span"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          window.open(row.document_url || row.fileUrl, "_blank", "noopener,noreferrer");
                        }}
                        sx={{ fontSize: 13, textDecoration: "underline", color: "#006EC2" }}
                      >
                        {t("requestDetails.viewDocument") || "View Document"}
                      </CustomText>
                      <CustomText sx={{ fontSize: 13, textDecoration: "underline", color: "#006EC2" }}>
                        {t("addDocument.form.browse") || "Replace"}
                      </CustomText>
                    </Box>
                  </Box>
                </label>
              ) : !formik.values.file ? (
                <label htmlFor="pdf-upload-edit" style={{ cursor: "pointer" }}>
                  <input
                    type="file"
                    id="pdf-upload-edit"
                    accept="application/pdf"
                    onChange={handleFileUpload}
                    hidden
                  />
                  <CustomText sx={{ fontSize: 14, fontWeight: 500 }}>
                    {t("addDocument.form.dragDrop")}{" "}
                    <span style={{ textDecoration: "underline", color: "#006EC2" }}>
                      {t("addDocument.form.browse")}
                    </span>
                  </CustomText>
                </label>
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    width: "100%",
                    justifyContent: "center",
                  }}
                >
                  <Box sx={{ textAlign: "center" }}>
                    <img src={document1} alt="PDF" style={{ width: 45 }} />
                    <CustomText sx={{ fontSize: 14 }}>
                      {formik.values.file.name}
                    </CustomText>
                    <CustomText sx={{ fontSize: 12, color: "#6B778C" }}>
                      {(formik.values.file.size / (1024 * 1024)).toFixed(2)} MB
                    </CustomText>
                  </Box>
                </Box>
              )}

              {formik.values.file && (
                <IconButton
                  size="small"
                  onClick={() => {
                    formik.setFieldValue("file", null);
                    formik.setFieldValue("fileUrl", null);
                    formik.setFieldTouched("file", true);
                  }}
                  sx={{
                    position: "absolute",
                    top: 8,
                    right: 8,
                    backgroundColor: "#fff",
                    border: "1px solid #E0E0E0",
                  }}
                >
                  <img src={profilecross} alt="remove" style={{ width: 20, height: 20 }} />
                </IconButton>
              )}
            </Box>
            {formik.touched.file && formik.errors.file && (
              <span style={{ color: "#d32f2f", fontSize: "12px", display: "block", marginTop: 4 }}>
                {formik.errors.file}
              </span>
            )}
            <Typography
              sx={{
                fontSize: 13,
                fontWeight: 500,
                fontFamily: "Poppins, sans-serif",
                textAlign: "left",
                color: "#6B778C",
              }}
            >
              {t("addDocument.validation.pdfOptional") || "Upload a new PDF to replace the existing file"}
            </Typography>
          </Box>

          {/* Buttons */}
          <Box sx={{ mt: 3, display: "flex", gap: 2, flexDirection: "column" }}>
            <CustomButton
              label={
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                  {loading && <CircularProgress size={18} sx={{ color: "#fff", mr: 1 }} />}
                  {t("common.save") || "Save Changes"}
                </Box>
              }
              onClick={formik.handleSubmit}
              size="small"
            />
          </Box>
          </>
        </Box>
      </Drawer>
    </>
  );
};

export default EditDrawer;
